import { ListIteratee, Many } from 'lodash';

import * as dto from 'client/dto';

import { LocationInfo } from 'components/_controls/AoiSearch/models';

import { VaccineLocationRow, VaccineTrackerCellData } from 'covidDataSuite/covidDataSuite.models';

import { DiseaseAndProximalRiskVM } from './DiseaseModels';

/**
 * @template V - Option value type, i.e. the literal key by which to sort
 */
export interface SortDropdownOption<V> {
  text: string;
  value: V;
}

/**
 * @template T - Type of array elements
 * @template V - Option value type, i.e. the literal key by which to sort
 */
export interface SortByOption<T, V> extends SortDropdownOption<V> {
  keys: Many<ListIteratee<T>>;
  orders: ('asc' | 'desc')[];
}

export type LocationListSortOptionValues = 'name' | 'country';
export const LocationListSortOptions: SortByOption<
  dto.GetGeonameModel,
  LocationListSortOptionValues
>[] = [
  {
    value: 'name',
    text: 'Alphabetical',
    keys: [x => x.name],
    orders: ['asc'],
  },
  {
    value: 'country',
    text: 'Country',
    keys: [x => x.country, x => x.name],
    orders: ['asc', 'asc'],
  },
];
export const locationListSortOptionByValue: (
  value: LocationListSortOptionValues
) => SortByOption<dto.GetGeonameModel, LocationListSortOptionValues> = (
  value: LocationListSortOptionValues
) => LocationListSortOptions.find(so => so.value === value);

export type DiseaseListSortOptionValues =
  | 'disease-name'
  | 'predicted-cases-of'
  | 'likelihood'
  | 'number-of-nearby-cases'
  | 'last-updated-date';

export const DiseaseListLocationViewSortOptions: SortByOption<
  DiseaseAndProximalRiskVM,
  DiseaseListSortOptionValues
>[] = [
  {
    value: 'disease-name',
    text: 'Alphabetical',
    keys: [x => x.disease.diseaseInformation.name],
    orders: ['asc'],
  },
  {
    value: 'predicted-cases-of',
    text: 'Estimated case importations',
    keys: [
      x => RiskModel2AvgMagnitudeForSorting(x.disease.importationRisk),
      x => x.disease.diseaseInformation.name,
    ],
    orders: ['desc', 'asc'],
  },
  {
    value: 'likelihood',
    text: 'Likelihood of case importation',
    keys: [
      x => RiskModel2AvgProbabilityForSorting(x.disease.importationRisk),
      x => x.disease.diseaseInformation.name,
    ],
    orders: ['desc', 'asc'],
  },
  {
    value: 'number-of-nearby-cases',
    text: 'Number of nearby cases',
    keys: [x => x.proximalVM && x.proximalVM.totalCases, x => x.disease.diseaseInformation.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'last-updated-date',
    text: 'Recently checked disease activity',
    keys: [x => x.disease.lastUpdatedEventDate],
    orders: ['desc'],
  },
];

export type DiseaseActivitiesSortModel = {
  name: string;
  id: number;
  cases: number;
  risk: dto.RiskModel;
  lastUpdatedEventDate: string;
};

export const DiseaseActivitiesSortOptions: SortByOption<
  DiseaseActivitiesSortModel,
  DiseaseListSortOptionValues
>[] = [
  {
    value: 'disease-name',
    text: 'Alphabetical',
    keys: [x => x.name],
    orders: ['asc'],
  },
  {
    value: 'predicted-cases-of',
    text: 'Estimated case importations',
    keys: [x => RiskModel2AvgMagnitudeForSorting(x.risk), x => x.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'likelihood',
    text: 'Likelihood of case importation',
    keys: [x => RiskModel2AvgProbabilityForSorting(x.risk), x => x.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'number-of-nearby-cases',
    text: 'Number of nearby cases',
    keys: [x => x.cases, x => x.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'last-updated-date',
    text: 'Recently checked disease activity',
    keys: [x => x.lastUpdatedEventDate],
    orders: ['desc'],
  },
];

export const diseaseActivitiesSortOptionByValue: (
  value: DiseaseListSortOptionValues
) => SortByOption<DiseaseActivitiesSortModel, DiseaseListSortOptionValues> = (
  value: DiseaseListSortOptionValues
) => DiseaseActivitiesSortOptions.find(so => so.value === value);

export const DiseaseActivitiesGlobalSortOptions: SortByOption<
  DiseaseActivitiesSortModel,
  DiseaseListSortOptionValues
>[] = [
  {
    value: 'disease-name',
    text: 'Alphabetical',
    keys: [x => x.name],
    orders: ['asc'],
  },
  {
    value: 'predicted-cases-of',
    text: 'Estimated case exportations',
    keys: [x => RiskModel2AvgMagnitudeForSorting(x.risk), x => x.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'likelihood',
    text: 'Likelihood of case exportation',
    keys: [x => RiskModel2AvgProbabilityForSorting(x.risk), x => x.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'number-of-nearby-cases',
    text: 'Number of reported cases',
    keys: [x => x.cases, x => x.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'last-updated-date',
    text: 'Recently checked disease activity',
    keys: [x => x.lastUpdatedEventDate],
    orders: ['desc'],
  },
];

export const DiseaseListGlobalViewSortOptions: SortByOption<
  DiseaseAndProximalRiskVM,
  DiseaseListSortOptionValues
>[] = [
  {
    value: 'disease-name',
    text: 'Alphabetical',
    keys: [x => x.disease.diseaseInformation.name],
    orders: ['asc'],
  },
  {
    value: 'predicted-cases-of',
    text: 'Estimated case exportations',
    keys: [
      x => RiskModel2AvgMagnitudeForSorting(x.disease.exportationRisk),
      x => x.disease.diseaseInformation.name,
    ],
    orders: ['desc', 'asc'],
  },
  {
    value: 'likelihood',
    text: 'Likelihood of case exportation',
    keys: [
      x => RiskModel2AvgProbabilityForSorting(x.disease.exportationRisk),
      x => x.disease.diseaseInformation.name,
    ],
    orders: ['desc', 'asc'],
  },
  {
    value: 'number-of-nearby-cases',
    text: 'Number of reported cases',
    keys: [x => x.caseCounts.reportedCases, x => x.disease.diseaseInformation.name],
    orders: ['desc', 'asc'],
  },
  {
    value: 'last-updated-date',
    text: 'Recently checked disease activity',
    keys: [x => x.disease.lastUpdatedEventDate],
    orders: ['desc'],
  },
];

export type EventListSortOptionValues =
  | 'event-title'
  | 'predicted-cases-of'
  | 'likelihood'
  | 'reported-cases'
  | 'reported-deaths'
  | 'last-updated-date';
export const EventListSortOptions: SortByOption<dto.GetEventModel, EventListSortOptionValues>[] = [
  {
    value: 'event-title',
    text: 'Alphabetical',
    keys: [x => x.eventInformation.title],
    orders: ['asc'],
  },
  {
    value: 'predicted-cases-of',
    text: 'Estimated case exportations',
    keys: [x => RiskModel2AvgMagnitudeForSorting(x.exportationRisk), x => x.eventInformation.title],
    orders: ['desc', 'asc'],
  },
  {
    value: 'likelihood',
    text: 'Exportation likelihood',
    keys: [
      x => RiskModel2AvgProbabilityForSorting(x.exportationRisk),
      x => x.eventInformation.title,
    ],
    orders: ['desc', 'asc'],
  },
  {
    value: 'reported-cases',
    text: 'Number of reported cases',
    keys: [x => x.caseCounts.reportedCases],
    orders: ['desc'],
  },
  {
    value: 'reported-deaths',
    text: 'Number of deaths',
    keys: [x => x.caseCounts.deaths],
    orders: ['desc'],
  },
  {
    value: 'last-updated-date',
    text: 'Recently checked disease activity',
    keys: [x => x.eventInformation.lastUpdatedDate],
    orders: ['desc'],
  },
];

export type DiseaseEventListViewSortOptionValues =
  | 'event-title'
  | 'predicted-cases-of'
  | 'likelihood'
  | 'reported-cases'
  | 'reported-deaths'
  | 'last-updated-date';
export const DiseaseEventListLocationViewSortOptions: SortByOption<
  dto.GetEventModel,
  DiseaseEventListViewSortOptionValues
>[] = [
  {
    value: 'event-title',
    text: 'Alphabetical',
    keys: [x => x.eventInformation.title],
    orders: ['asc'],
  },
  {
    value: 'predicted-cases-of',
    text: 'Estimated case importations',
    keys: [x => RiskModel2AvgMagnitudeForSorting(x.importationRisk), x => x.eventInformation.title],
    orders: ['desc', 'asc'],
  },
  {
    value: 'likelihood',
    text: 'Likelihood of case importation',
    keys: [
      x => RiskModel2AvgProbabilityForSorting(x.importationRisk),
      x => x.eventInformation.title,
    ],
    orders: ['desc', 'asc'],
  },
  {
    value: 'reported-cases',
    text: 'Number of reported cases',
    keys: [x => x.caseCounts.reportedCases],
    orders: ['desc'],
  },
  {
    value: 'reported-deaths',
    text: 'Number of reported deaths',
    keys: [x => x.caseCounts.deaths],
    orders: ['desc'],
  },
  {
    value: 'last-updated-date',
    text: 'Recently checked disease activity',
    keys: [x => x.eventInformation.lastUpdatedDate],
    orders: ['desc'],
  },
];

export const DiseaseEventListGlobalViewSortOptions: SortByOption<
  dto.GetEventModel,
  DiseaseEventListViewSortOptionValues
>[] = [
  {
    value: 'event-title',
    text: 'Alphabetical',
    keys: [x => x.eventInformation.title],
    orders: ['asc'],
  },
  {
    value: 'predicted-cases-of',
    text: 'Estimated case exportations',
    keys: [x => RiskModel2AvgMagnitudeForSorting(x.exportationRisk), x => x.eventInformation.title],
    orders: ['desc', 'asc'],
  },
  {
    value: 'likelihood',
    text: 'Likelihood of case exportation',
    keys: [
      x => RiskModel2AvgProbabilityForSorting(x.exportationRisk),
      x => x.eventInformation.title,
    ],
    orders: ['desc', 'asc'],
  },
  {
    value: 'reported-cases',
    text: 'Number of reported cases',
    keys: [x => x.caseCounts.reportedCases],
    orders: ['desc'],
  },
  {
    value: 'reported-deaths',
    text: 'Number of reported deaths',
    keys: [x => x.caseCounts.deaths],
    orders: ['desc'],
  },
  {
    value: 'last-updated-date',
    text: 'Recently checked disease activity',
    keys: [x => x.eventInformation.lastUpdatedDate],
    orders: ['desc'],
  },
];

type SortOptionValuesIntersection = DiseaseListSortOptionValues &
  DiseaseEventListViewSortOptionValues;

export const DefaultSortOptionValue: SortOptionValuesIntersection = 'predicted-cases-of';

const RiskFeedSortTypeLabels: Record<dto.RiskFeedSortType, string> = {
  [dto.RiskFeedSortType.MostAtRisk]: 'Highest weighted risk',
  [dto.RiskFeedSortType.CaseIncidence]: 'Case incidence: High to low',
  [dto.RiskFeedSortType.ImportationRisk]: 'Risk of importation: High to low',
  [dto.RiskFeedSortType.Severity]: 'Disease severity: High to low',
  [dto.RiskFeedSortType.Preventability]: 'Disease preventibility: Low to high',
  [dto.RiskFeedSortType.Transmissibility]: 'Disease transmissibility: High to low',
  [dto.RiskFeedSortType.LocationPreparedness]: 'Location preparedness: Low to high',
};

export type DiseaseActivitiesRelevantSortModel = {
  id: number;
  name: string;
  entryPriorityScore: {
    riskPriorityScore: number;
    caseIncidence: number;
    importationRisk: number;
  };
  riskPriorityScore?: number;
  caseIncidence?: number;
  globalReportedCases?: number;
  importationRisk?: dto.RiskModel;
  exportationRisk?: dto.RiskModel;
};

export const DefaultDiseaseActivitiesRelevantSortOptionValue = dto.RiskFeedSortType.MostAtRisk;
export const DiseaseActivitiesRelevantSortOptions: SortByOption<
  DiseaseActivitiesRelevantSortModel,
  dto.RiskFeedSortType
>[] = [
  {
    value: dto.RiskFeedSortType.MostAtRisk,
    text: RiskFeedSortTypeLabels[dto.RiskFeedSortType.MostAtRisk],
    keys: [
      x => x.riskPriorityScore,
      x => x.entryPriorityScore.riskPriorityScore,
      x => x.globalReportedCases,
      x => RiskModel2AvgMagnitudeForSorting(x.exportationRisk),
      x => RiskModel2AvgProbabilityForSorting(x.exportationRisk),
      x => x.name,
    ],
    orders: ['desc', 'desc', 'desc', 'desc', 'desc', 'asc'],
  },
  {
    value: dto.RiskFeedSortType.CaseIncidence,
    text: RiskFeedSortTypeLabels[dto.RiskFeedSortType.CaseIncidence],
    keys: [
      x => x.caseIncidence,
      x => x.entryPriorityScore.caseIncidence,
      x => x.globalReportedCases,
      x => RiskModel2AvgMagnitudeForSorting(x.exportationRisk),
      x => RiskModel2AvgProbabilityForSorting(x.exportationRisk),
      x => x.name,
    ],
    orders: ['desc', 'desc', 'desc', 'desc', 'desc', 'asc'],
  },
  {
    value: dto.RiskFeedSortType.ImportationRisk,
    text: RiskFeedSortTypeLabels[dto.RiskFeedSortType.ImportationRisk],
    keys: [
      x => RiskModel2AvgMagnitudeForSorting(x.importationRisk),
      x => RiskModel2AvgProbabilityForSorting(x.importationRisk),
      x => x.entryPriorityScore.importationRisk,
      x => x.globalReportedCases,
      x => RiskModel2AvgMagnitudeForSorting(x.exportationRisk),
      x => RiskModel2AvgProbabilityForSorting(x.exportationRisk),
      x => x.name,
    ],
    orders: ['desc', 'desc', 'desc', 'desc', 'desc', 'desc', 'asc'],
  },
];
export const DiseaseActivitesRelevantSortOptionByValue: (
  value: dto.RiskFeedSortType
) => SortByOption<DiseaseActivitiesRelevantSortModel, dto.RiskFeedSortType> = (
  value: dto.RiskFeedSortType
) => DiseaseActivitiesRelevantSortOptions.find(so => so.value === value);

export type RiskOverviewSortType = {
  value: dto.RiskFeedSortType;
  text: string;
};

export const RiskOverviewSortOptions: RiskOverviewSortType[] = [
  {
    value: dto.RiskFeedSortType.MostAtRisk,
    text: RiskFeedSortTypeLabels[dto.RiskFeedSortType.MostAtRisk],
  },
  {
    value: dto.RiskFeedSortType.CaseIncidence,
    text: RiskFeedSortTypeLabels[dto.RiskFeedSortType.CaseIncidence],
  },
  {
    value: dto.RiskFeedSortType.ImportationRisk,
    text: RiskFeedSortTypeLabels[dto.RiskFeedSortType.ImportationRisk],
  },
];

function RiskModel2AvgMagnitudeForSorting(risk: dto.RiskModel) {
  return !risk || risk.isModelNotRun || risk.isModelRunning
    ? -1
    : (risk.minMagnitude + risk.maxMagnitude) / 2;
}

function RiskModel2AvgProbabilityForSorting(risk: dto.RiskModel) {
  return !risk || risk.isModelNotRun || risk.isModelRunning
    ? -1
    : (risk.minProbability + risk.maxProbability) / 2;
}

export const DefaultLocationInfoSortFns: Many<ListIteratee<LocationInfo>> = [
  x => x.countryName,
  x => x.provinceName ?? '',
  x => x.name,
];
export const DefaultLocationInfoSortOrders: ('asc' | 'desc')[] = ['asc', 'asc', 'asc'];

export type VaccineSortOptionValues =
  | 'stringency-index-score'
  | 'stringency-change-30days'
  | 'highrisk-pop-vac-desc'
  | 'total-pop-vac-desc'
  | 'local-daily'
  | 'local-positivity';
const DefaultVaccineSortFns: Many<ListIteratee<VaccineLocationRow>> = [
  x => x.countryName,
  x => x.provinceName ?? '',
  x => x.name,
];
const DefaultVaccineSortOrders: ('asc' | 'desc')[] = ['asc', 'asc', 'asc'];

const sortValueDailyRateDesc = (d: VaccineTrackerCellData) =>
  !d?.isProjectionReliable ? -1 : d?.hasAchievedMilestone ? 0 : d?.data?.dailyVaccinationRate ?? 0;

export type VaccineSortByOption = SortByOption<VaccineLocationRow, VaccineSortOptionValues>;
export const VaccineSortOptions: {
  containment: VaccineSortByOption[];
  totalAdult: VaccineSortByOption[];
  localCases: VaccineSortByOption[];
} = {
  containment: [
    {
      value: 'stringency-index-score',
      text: 'Containment and health index score',
      keys: [
        x => x.stringency?.data?.stringencyIndex ?? Number.MIN_VALUE,
        ...DefaultVaccineSortFns,
      ],
      orders: ['desc', ...DefaultVaccineSortOrders],
    },
    {
      value: 'stringency-change-30days',
      text: 'Change over the past 30 days',
      keys: [
        x => x.stringency?.data?.stringencyIndexChange ?? Number.MIN_VALUE,
        ...DefaultVaccineSortFns,
      ],
      orders: ['desc', ...DefaultVaccineSortOrders],
    },
  ],
  totalAdult: [
    {
      value: 'total-pop-vac-desc',
      text: '% of total population fully vaccinated',
      keys: [
        x => x.totalAdult?.data?.totalFullVaccinationRate ?? Number.MIN_VALUE,
        x => sortValueDailyRateDesc(x.totalAdult),
        ...DefaultVaccineSortFns,
      ],
      orders: ['desc', 'desc', ...DefaultVaccineSortOrders],
    },
    {
      value: 'highrisk-pop-vac-desc',
      text: '% of estimated high-risk population fully vaccinated',
      keys: [
        x => x.totalAdult?.secondaryData?.data?.totalFullVaccinationRate ?? Number.MIN_VALUE,
        x => sortValueDailyRateDesc(x.totalAdult?.secondaryData),
        ...DefaultVaccineSortFns,
      ],
      orders: ['desc', 'desc', ...DefaultVaccineSortOrders],
    },
  ],
  localCases: [
    {
      value: 'local-daily',
      text: 'Daily cases / 100,000',
      keys: [
        x => x?.totalAdult?.data?.dailyNewCasesPer100000 ?? Number.MAX_VALUE,
        ...DefaultVaccineSortFns,
      ],
      orders: ['asc', ...DefaultVaccineSortOrders],
    },
    {
      value: 'local-positivity',
      text: 'Test positivity (last 14 days)',
      keys: [
        x => x?.totalAdult?.data?.testPositivityLast14Days ?? Number.MAX_VALUE,
        ...DefaultVaccineSortFns,
      ],
      orders: ['asc', ...DefaultVaccineSortOrders],
    },
  ],
};
export const VaccineSortOptionsFlat = [
  ...VaccineSortOptions.localCases,
  ...VaccineSortOptions.totalAdult,
  ...VaccineSortOptions.containment,
];
